import { Phone } from "lucide-react";
import { useContent } from "@/i18n";
import { firm, telHref } from "@/config/firm.config";
import { Reveal } from "./Reveal";
import { SectionHeading } from "./SectionHeading";

const Portrait = ({ src, name }) => (
  <div className="relative aspect-[4/5] w-full overflow-hidden border border-gold/20">
    <img
      src={src}
      alt={name}
      loading="lazy"
      className="h-full w-full object-cover object-top grayscale-[35%] transition-[transform,filter] duration-[1.2s] ease-out group-hover:scale-[1.04] group-hover:grayscale-0"
    />
    <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(180deg,rgba(11,16,35,0)_45%,rgba(11,16,35,0.85)_100%)]" />
    <span aria-hidden="true" className="absolute -bottom-2 -right-2 h-10 w-10 border-b border-r border-gold/70 transition-transform duration-700 group-hover:translate-x-1 group-hover:translate-y-1" />
  </div>
);

export const Advocates = () => {
  const { t } = useContent();
  return (
    <section id="advocates" data-testid="advocates-section" className="relative bg-navy-800 py-28 md:py-40">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <SectionHeading eyebrow={t.advocates.eyebrow} title={t.advocates.title} />
        <ul className="mt-20 grid gap-6 md:grid-cols-3" data-testid="advocates-grid">
          {firm.advocates.map((a, i) => (
            <Reveal key={a.id} delay={0.1 * i} as="li" className="h-full">
              <div
                data-testid={`advocate-card-${a.id}`}
                className="group flex h-full flex-col border border-white/10 bg-navy-900/60 p-8 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-[0_30px_60px_-30px_rgba(0,0,0,0.8)] md:p-10"
              >
                <Portrait src={a.photo} name={a.name} />
                <h3 className="mt-8 font-serif text-3xl text-paper" data-testid={`advocate-name-${a.id}`}>
                  {a.name}
                </h3>
                <p className="mt-2 text-sm tracking-wide text-gold">{t.advocates.role}</p>
                <div className="mt-8 border-t border-white/10 pt-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">{t.advocates.qualifications}</p>
                  <p className="mt-3 font-serif text-lg leading-relaxed text-paper/90" data-testid={`advocate-degrees-${a.id}`}>
                    {a.degrees.join(" · ")}
                  </p>
                </div>
                <a
                  href={telHref(a.phone)}
                  data-testid={`advocate-phone-${a.id}`}
                  className="focus-ring mt-auto inline-flex items-center gap-3 pt-10 text-sm text-ink transition-colors duration-300 hover:text-gold-light"
                  aria-label={`${t.advocates.call} ${a.name} at ${a.phone}`}
                >
                  <Phone size={14} aria-hidden="true" />
                  <span className="tracking-wide">{a.phone}</span>
                </a>
              </div>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
};
